import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type ChatChannelMemberResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { UserActionDialogProvider } from './UserActionDialogHost';
import { UserActionMenu } from './UserActionMenu';
import { UserAvatar } from './UserAvatar';

/** Role order for the roster — owners and admins first, then everyone by name. */
const ROLE_RANK: Record<string, number> = { owner: 0, admin: 1, moderator: 2, member: 3 };

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  moderator: 'Mod',
};

interface ChannelMembersPopoverProps {
  channelId: string;
  members: ChatChannelMemberResponse[];
  memberCount: number;
  currentUserId?: string | null;
  onMessage?: (userId: string) => void | Promise<void>;
  onMention?: (userId: string) => void;
  /**
   * Removes a member from this channel. Callers pass it only when the viewer
   * holds the permission — the popover shows the action, it does not authorize it.
   */
  onRemoveMember?: (userId: string) => void | Promise<void>;
}

/**
 * Expands the header's member count into the roster, with the standard person
 * menu on each name.
 *
 * `memberCount` and `members` are separate on purpose: the count is the channel's
 * true total from the server, while `members` is the page the client has loaded.
 * Showing the count as the trigger while listing what we actually have avoids
 * claiming a roster is complete when it is only the first page.
 */
export function ChannelMembersPopover({
  channelId,
  members,
  memberCount,
  currentUserId,
  onMessage,
  onMention,
  onRemoveMember,
}: ChannelMembersPopoverProps) {
  const [search, setSearch] = useState('');

  const sorted = useMemo(() => {
    const name = (m: ChatChannelMemberResponse) =>
      (m.nickname || m.displayName || m.username || m.userId).toLocaleLowerCase();
    // Filters the LOADED page only — the roster is one page deep, so the empty
    // state below says so rather than implying nobody matches in the channel.
    const needle = search.trim().toLocaleLowerCase();
    const matching = needle
      ? members.filter(
          (m) =>
            name(m).includes(needle) || (m.username ?? '').toLocaleLowerCase().includes(needle),
        )
      : members;
    return [...matching].sort((a, b) => {
      const rank = (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9);
      return rank !== 0 ? rank : name(a).localeCompare(name(b));
    });
  }, [members, search]);

  const label = `${memberCount} member${memberCount !== 1 ? 's' : ''}`;

  return (
    // The provider sits ABOVE the Popover deliberately: PopoverContent unmounts
    // its children on close, so dialogs owned inside it would disappear the
    // moment the popover dismissed behind them.
    <UserActionDialogProvider onOpenDm={onMessage ? (uid) => void onMessage(uid) : undefined}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label={`Show members — ${label}`}
          >
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-0">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-foreground">Members</p>
            {/* Only worth saying when the loaded page is short of the total. */}
            {members.length < memberCount && (
              <p className="text-[11px] text-muted-foreground">
                Showing {members.length} of {memberCount}
              </p>
            )}
            {/* Only worth offering once the list is long enough to scan badly. */}
            {members.length > 8 && (
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search members"
                aria-label="Search members"
                className="mt-2 w-full rounded border border-border bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            )}
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {sorted.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground">
                {search.trim() ? 'No match in the loaded members.' : 'No members loaded.'}
              </p>
            ) : (
              sorted.map((member) => {
                const displayName =
                  member.nickname || member.displayName || member.username || 'Unknown';
                const roleLabel = ROLE_LABEL[member.role];
                const isSelf = member.userId === currentUserId;
                return (
                  <UserActionMenu
                    key={member.userId}
                    userId={member.userId}
                    displayName={displayName}
                    username={member.username}
                    currentUserId={currentUserId}
                    channelId={channelId}
                    onMessage={onMessage}
                    onMention={onMention}
                    // Removing yourself is "leave channel", which lives in
                    // channel settings — never offer it here.
                    onRemoveFromGroup={!isSelf ? onRemoveMember : undefined}
                    removeLabel="Remove from channel"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted transition-colors"
                    >
                      <UserAvatar
                        userId={member.userId}
                        displayName={displayName}
                        initial={displayName.charAt(0).toUpperCase()}
                        className="h-6 w-6 flex-shrink-0"
                        fallbackClassName="text-[10px] font-semibold text-white"
                      />
                      <span className="flex-1 min-w-0 truncate text-xs text-foreground">
                        {displayName}
                        {isSelf && <span className="text-muted-foreground"> (you)</span>}
                      </span>
                      {roleLabel && (
                        <span
                          className={cn(
                            'text-[10px] px-1.5 py-0.5 rounded-full border border-border',
                            'text-muted-foreground flex-shrink-0',
                          )}
                        >
                          {roleLabel}
                        </span>
                      )}
                    </button>
                  </UserActionMenu>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </UserActionDialogProvider>
  );
}
